/**
 * ============================================================================
 * FILE UPLOAD COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Universal file upload component with drag & drop
 */

import React, { useState, useCallback } from 'react';
import { useStorage } from '../../hooks/useStorage';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import ProgressBar from '../common/ProgressBar';

/**
 * File Upload Component
 * @param {Object} props
 * @param {Function} props.onUploadComplete - Callback with uploaded file data
 * @param {Function} props.onUploadError - Error callback
 * @param {string} props.folder - Upload folder path
 * @param {Array} props.accept - Accepted file types (e.g., ['image/*', '.pdf'])
 * @param {number} props.maxSize - Max file size in bytes
 * @param {number} props.maxFiles - Maximum number of files
 * @param {boolean} props.multiple - Allow multiple files
 * @param {boolean} props.required - Is upload required
 * @param {string} props.label - Upload label
 * @returns {JSX.Element}
 */
const FileUpload = ({
  onUploadComplete,
  onUploadError,
  folder = 'uploads',
  accept = ['*/*'],
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = 1,
  multiple = false,
  required = false,
  label,
}) => {
  const { uploadFile, uploadMultipleFiles, uploadProgress, loading } = useStorage();
  const { showSuccess, showError } = useToast();
  const { language } = useI18n();

  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState({});

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const validateFile = (file) => {
    const errors = [];

    // Check file size
    if (file.size > maxSize) {
      errors.push(
        language === 'fr'
          ? `Le fichier dépasse la taille maximale de ${formatFileSize(maxSize)}`
          : `File exceeds maximum size of ${formatFileSize(maxSize)}`
      );
    }

    // Check file type if accept is specified
    if (accept.length > 0 && !accept.includes('*/*')) {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const fileType = file.type;
      const isAccepted = accept.some((pattern) => {
        if (pattern.startsWith('.')) {
          return pattern === fileExtension;
        }
        if (pattern.includes('/*')) {
          const baseType = pattern.split('/')[0];
          return fileType.startsWith(baseType + '/');
        }
        return pattern === fileType;
      });

      if (!isAccepted) {
        errors.push(
          language === 'fr'
            ? 'Type de fichier non autorisé'
            : 'File type not allowed'
        );
      }
    }

    return errors;
  };

  const handleFileSelect = useCallback(
    async (selectedFiles) => {
      const fileArray = Array.from(selectedFiles);
      const totalFiles = files.length + fileArray.length;

      if (totalFiles > maxFiles) {
        showError(
          language === 'fr'
            ? `Maximum ${maxFiles} fichier(s) autorisé(s)`
            : `Maximum ${maxFiles} file(s) allowed`
        );
        return;
      }

      // Validate all files
      const validationErrors = {};
      fileArray.forEach((file, index) => {
        const errors = validateFile(file);
        if (errors.length > 0) {
          validationErrors[file.name] = errors;
        }
      });

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        showError(
          language === 'fr'
            ? 'Certains fichiers ne sont pas valides'
            : 'Some files are invalid'
        );
        return;
      }

      setFiles((prev) => [...prev, ...fileArray]);
      setErrors({});

      // Auto-upload if files are valid
      try {
        if (multiple || maxFiles > 1) {
          const results = await uploadMultipleFiles(fileArray, { folder });
          const successful = results.filter((r) => r.success);
          setUploadedFiles((prev) => [...prev, ...successful.map((r) => r.data)]);
          if (onUploadComplete) {
            onUploadComplete(successful.map((r) => r.data));
          }
          showSuccess(
            language === 'fr'
              ? `${successful.length} fichier(s) téléchargé(s)`
              : `${successful.length} file(s) uploaded`
          );
        } else {
          const result = await uploadFile(fileArray[0], { folder });
          setUploadedFiles((prev) => [...prev, result]);
          if (onUploadComplete) {
            onUploadComplete(result);
          }
          showSuccess(
            language === 'fr' ? 'Fichier téléchargé' : 'File uploaded'
          );
        }
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du téléchargement' : 'Upload failed')
        );
        if (onUploadError) {
          onUploadError(error);
        }
      }
    },
    [files, maxFiles, multiple, folder, uploadFile, uploadMultipleFiles, onUploadComplete, onUploadError, showSuccess, showError, language]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        handleFileSelect(droppedFiles);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    const type = file.type || '';
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    return '📎';
  };

  return (
    <div className="space-y-4">
      {/* Label */}
      {label && (
        <label className="block text-sm font-black text-gray-900 dark:text-white">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Drop Zone */}
      <Card
        variant="outlined"
        className={`${
          isDragging
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600'
        } transition-all duration-300`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <label className="block cursor-pointer">
          <input
            type="file"
            multiple={multiple || maxFiles > 1}
            accept={accept.join(',')}
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
            disabled={loading}
          />
          <div className="flex flex-col items-center justify-center p-12 text-center">
            {loading ? (
              <>
                <Spinner size="md" className="mb-4" />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {language === 'fr' ? 'Téléchargement...' : 'Uploading...'}
                </p>
                {uploadProgress > 0 && (
                  <div className="mt-4 w-full max-w-xs">
                    <ProgressBar
                      value={uploadProgress}
                      variant="info"
                      animated
                      showLabel
                      label={`${uploadProgress}%`}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <span className="text-6xl mb-4 animate-bounce">📤</span>
                <p className="text-lg font-black text-gray-900 dark:text-white mb-2">
                  {language === 'fr' ? 'Glisser-déposer des fichiers' : 'Drag & Drop Files'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {language === 'fr' ? 'ou' : 'or'}
                </p>
                <Button variant="outline" type="button">
                  {language === 'fr' ? 'Choisir des fichiers' : 'Choose Files'}
                </Button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                  {language === 'fr'
                    ? `Maximum ${formatFileSize(maxSize)} par fichier`
                    : `Maximum ${formatFileSize(maxSize)} per file`}
                  {maxFiles > 1 &&
                    ` • ${language === 'fr' ? 'Maximum' : 'Max'} ${maxFiles} ${language === 'fr' ? 'fichiers' : 'files'}`}
                </p>
              </>
            )}
          </div>
        </label>
      </Card>

      {/* Upload Progress */}
      {loading && uploadProgress > 0 && (
        <Card variant="outlined" className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-blue-800 dark:text-blue-200">
                {language === 'fr' ? 'Téléchargement en cours...' : 'Uploading...'}
              </span>
              <span className="font-bold text-blue-600 dark:text-blue-400">
                {uploadProgress}%
              </span>
            </div>
            <ProgressBar
              value={uploadProgress}
              variant="info"
              animated
              showLabel={false}
            />
          </div>
        </Card>
      )}

      {/* Selected Files */}
      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {language === 'fr' ? 'Fichiers sélectionnés:' : 'Selected Files:'}
          </p>
          {files.map((file, index) => (
            <Card
              key={index}
              variant="outlined"
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 animate-fadeInUp"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <span className="text-2xl">{getFileIcon(file)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(file.size)}
                </p>
              </div>
              {errors[file.name] && (
                <div className="text-xs text-red-600 dark:text-red-400">
                  {errors[file.name].join(', ')}
                </div>
              )}
              {uploadedFiles[index] && (
                <span className="text-green-500 text-xl">✓</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveFile(index)}
                className="text-red-600 hover:text-red-700 dark:text-red-400"
              >
                ✕
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {language === 'fr' ? 'Fichiers téléchargés:' : 'Uploaded Files:'}
          </p>
          {uploadedFiles.map((file, index) => (
            <Card
              key={index}
              variant="outlined"
              className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 animate-fadeInUp"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <span className="text-2xl">✓</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {file.fileName || file.name || `File ${index + 1}`}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {file.url || file.path}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  window.open(file.url || file.path, '_blank', 'noopener,noreferrer');
                }}
              >
                {language === 'fr' ? 'Ouvrir' : 'Open'} →
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
